import type {
  FactoryOptionDto,
  MachineAttachmentDto,
  MachineAttachmentItemDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineDto,
  MachineItemPriceOnlyDto,
  MachineQuotationItemDto
} from '@api/machines'
import { ARCHIVED_ITEMS, displayOldPrice, EqOptionType } from '@common/utils'
import {
  type IconColor,
  type IconVariant,
  MolsonAccordion,
  MolsonBox
} from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { type ChangeEvent, useEffect, useState } from 'react'
import { FactoryOption } from '../factory-option/FactoryOption'
import type {
  FactoryOptionDtoWithChecked,
  HandleOptionChangeFunctionProps
} from '../types'

export type FactoryFitsOptionsProps = {
  archivedFactoryFits: FactoryOptionDtoWithChecked[]
  expandedFamilies?: Set<string>
  factoryFits: FactoryOptionDto[]
  isFamily: boolean
  isGroup: boolean
  setTypeName: string
  handleAddFactoryOption: (
    event: ChangeEvent<HTMLInputElement>,
    factoryOption: FactoryOptionDto
  ) => void
  handleOptionChange: HandleOptionChangeFunctionProps
  machine?: MachineAttachmentDto | MachineDto
  quotationItem?: MachineAttachmentItemDto | MachineQuotationItemDto
  quotationItemOldPrices: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
  setArchivedFactoryFits: (values: FactoryOptionDtoWithChecked[]) => void
  setExpandedFamilies?: (value: string) => void
  closeExpandedFamilyOrGroup?: (
    eqOptionType: EqOptionType,
    familyOrGroup: string
  ) => void
  setFactoryFitsNames?: (values: string[]) => void
}

export const FactoryFitsOptions = ({
  archivedFactoryFits,
  expandedFamilies,
  factoryFits,
  setTypeName,
  isFamily,
  isGroup,
  handleAddFactoryOption,
  handleOptionChange,
  machine,
  quotationItem,
  quotationItemOldPrices,
  setArchivedFactoryFits,
  setExpandedFamilies,
  setFactoryFitsNames,
  closeExpandedFamilyOrGroup
}: FactoryFitsOptionsProps) => {
  const theme = molsonUseTheme()
  const [expanded, setExpanded] = useState(expandedFamilies?.has(setTypeName))

  const itemKey = `${ARCHIVED_ITEMS.FACTORY_FIT}_${setTypeName}`

  const archivedFamilies = quotationItem?.factoryOptionItems
    ?.filter(option => option.isArchived)
    .map(archivedItem => archivedItem.family)

  const selectedFactoryOptions = quotationItem?.factoryOptionItems?.filter(
    option =>
      (isFamily && option.family === setTypeName) ||
      (isGroup && option.group === setTypeName)
  )

  let hasFamilyPriceDifference = false
  if (
    selectedFactoryOptions?.length &&
    quotationItemOldPrices?.factoryOptionItems?.length
  ) {
    for (const option of selectedFactoryOptions) {
      const oldOption = quotationItemOldPrices.factoryOptionItems.find(
        oldItem => oldItem.id === option.id
      )

      if (displayOldPrice(option.dnp ?? 0, oldOption?.dnp ?? 0)) {
        hasFamilyPriceDifference = true
        break
      }
    }
  }

  const getSummaryIcon = () => {
    if (archivedFamilies?.includes(setTypeName)) {
      return { icon: 'ERROR_OUTLINE', color: 'error' }
    }
    if (hasFamilyPriceDifference) {
      return { icon: 'WARNING', color: 'warning' }
    }
    return undefined
  }

  const summaryIcon = getSummaryIcon()

  const selectedCount = selectedFactoryOptions?.length ?? 0

  const countBadge =
    selectedCount > 0 ? (
      <MolsonBox
        component="span"
        sx={{
          backgroundColor: theme.palette.primary.main,
          borderRadius: 100,
          color: theme.palette.secondary[50],
          fontSize: 16,
          fontWeight: 600,
          width: 30,
          height: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {selectedCount}
      </MolsonBox>
    ) : undefined

  useEffect(() => {
    setExpanded(expandedFamilies?.has(setTypeName))
  }, [expandedFamilies, setTypeName])

  return (
    <MolsonAccordion
      onChange={(_, toggle) => {
        setExpandedFamilies?.(itemKey)
        if (!toggle) {
          closeExpandedFamilyOrGroup?.(EqOptionType.FACTORY_FIT, setTypeName)
        }
        setExpanded(toggle)
      }}
      summaryIcon={summaryIcon?.icon as IconVariant}
      summaryIconColor={summaryIcon?.color as IconColor}
      additionalSummaryComponent={countBadge}
      expanded={expandedFamilies?.has(itemKey) || expanded}
      primaryText={setTypeName}
    >
      <FactoryOption
        archivedFactoryFits={archivedFactoryFits}
        factoryFits={factoryFits}
        setTypeName={setTypeName}
        isFamily={isFamily}
        isGroup={isGroup}
        handleAddFactoryOption={handleAddFactoryOption}
        handleOptionChange={handleOptionChange}
        machine={machine}
        setFactoryFitsNames={setFactoryFitsNames}
        quotationItem={quotationItem}
        quotationItemOldPrices={quotationItemOldPrices}
        setArchivedFactoryFits={setArchivedFactoryFits}
      />
    </MolsonAccordion>
  )
}
